import { ApiConstant, AppConstant, KeyConstant, SystemConstant } from "const";
import { call, put, select } from "redux-saga/effects";
import store, { AuthActions, BranchActions, PubSubActions } from "redux-store";
import { AuthService, ElectronService, getInstanceApi } from "services";
import { LocalDbManagement, LocalKeyActionService } from "services/local.service";
import { StorageUtil, getPrefixKey, toCamel, toSnake, uuid } from "utils";
import { getLoginBranchInfo, handlingLogoutBranch, setLoginData } from "utils/auth.utils";
import { subscribeRedisPubsub } from "../services/redisService";
import { synchronizeData } from "./synchronize.saga";

const getSupportEncryptTypeProtocols = window.bglobalSignal.libs.EncryptTypeProtocol.getSupportEncryptTypeProtocols;

export function* requestLogin(action) {
  try {
    yield put(
      AuthActions.authSet({
        authLoginStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.pending,
      }),
    );

    const { data } = action;

    const payload = { ...data };
    const encryptTypeProtocols = getSupportEncryptTypeProtocols();
    payload.encryptionTypeProtocols = encryptTypeProtocols;
    const params = toSnake(payload);
    const currentLoginInfo = StorageUtil.getCommonKey(KeyConstant.KEY_TMP_BRANCH);
    if (currentLoginInfo.id !== SystemConstant.GLOBAL_BRANCH_ID) {
      const checkResponse = yield call(AuthService.checkRequestLogin, currentLoginInfo.domain, currentLoginInfo.phone);

      if (checkResponse.status !== ApiConstant.STT_ACCEPTED) {
        yield handleErrorResponse(checkResponse, STATE_CONTROL_NAME.login);
        return;
      }
    }

    const response = yield call(AuthService.login, params);
    const dataLogin = toCamel(response.data);
    if (response.status === ApiConstant.STT_OK && dataLogin) {
      yield put(
        AuthActions.authSuccess({
          isSuccess: true,
          phoneNumber: data.phone,
          accountUuid: data.accountUuid,
          deviceUuid: data.deviceUuid,
          token: data.token,
          isNew: response.data.isNew,
          masterExisted: dataLogin.masterExisted,
          masterName: response.data.master_name,
          authLoginStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.success,
          authVerifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.notStart,
        }),
      );
    } else {
      yield handleErrorResponse(response, STATE_CONTROL_NAME.login);
    }
  } catch (error) {
    yield put(AuthActions.authFailure(error));
  }
}

export function* requestVerify(action) {
  try {
    yield put(
      AuthActions.authSet({
        authVerifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.pending,
      }),
    );

    const {
      authRedux: { token, accountUuid, deviceUuid, phoneNumber },
    } = yield select();
    const { data } = action;
    const keys = yield LocalKeyActionService.getAllKeys();
    const encryptTypeProtocols = getSupportEncryptTypeProtocols();

    const params = {
      ...data,
      token: data.token || token,
      account_uuid: data.account_uuid || accountUuid,
      device_uuid: data.device_uuid || deviceUuid,
      encryption_type_protocols: encryptTypeProtocols,
      ...keys,
    };
    const response = yield call(AuthService.verify, params);
    if (response.status === ApiConstant.STT_OK) {
      const camelResponse = toCamel(response.data);
      const isResult = yield handleLogin(camelResponse, data.phone || phoneNumber);
      if (false === isResult) throw new Error("CAN NOT LOGIN");

      if (false === Boolean(data.verify_token)) {
        // Case: Login page
        yield put(
          AuthActions.authSuccess({
            isLogin: true,
            authVerifyStatus: AppConstant.VERIFY_OTP_ERROR_TYPE.success,
          }),
        );
      }

      yield synchronizeData();
    } else {
      yield handleErrorResponse(response, STATE_CONTROL_NAME.authVerify);
    }
  } catch (error) {
    yield handleErrorResponse({ error }, STATE_CONTROL_NAME.authVerify);
  }
}

const handleLogin = async (camelResponse, phoneNumber) => {
  const prefixKey = getPrefixKey(camelResponse.accountId, camelResponse.branchId);
  try {
    const isValid =
      Object.keys(camelResponse).length > 0 && phoneNumber && prefixKey && false === prefixKey.includes("undefined");
    if (!isValid) return false;

    setLoginData(camelResponse, phoneNumber, prefixKey);
    // Set token to api's current branch
    getInstanceApi(prefixKey).setToken(camelResponse.accessToken);

    // create db for new user in this device
    await ElectronService.initDatabaseForUser(prefixKey);
    const currentDbRecord = await LocalDbManagement.findOne({
      branch_id: camelResponse.branchId,
    });

    const timestamp = new Date().getTime();
    const loginBranchInfo = getLoginBranchInfo();
    const dbManagementItem = Boolean(currentDbRecord && currentDbRecord.id)
      ? {
          ...currentDbRecord,
          owner_name: phoneNumber,
          account_id: camelResponse.accountId,
          token: camelResponse.accessToken,
          options: JSON.stringify(camelResponse),
          state: SystemConstant.STATE.active,
          modified: timestamp,
        }
      : {
          id: uuid(),
          account_id: camelResponse.accountId,
          branch_id: camelResponse.branchId,
          owner_name: phoneNumber,
          state: SystemConstant.STATE.active,
          options: JSON.stringify(camelResponse),
          token: camelResponse.accessToken,
          branch_name: null,
          branch_domain: loginBranchInfo.domain,
          logo_app: null,
          created: timestamp,
          modified: timestamp,
        };
    await LocalDbManagement.save([dbManagementItem]);

    // Update new device id in message.table
    // const newDeviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);
    // await getInteractor(prefixKey).LocalMessageService.updateNewDevice(newDeviceId);

    BranchActions.updateSelectedBranch(StorageUtil.getCommonKey(KeyConstant.KEY_SELECTED_SERVER));

    // Set app logo
    ElectronService.setLogoApp(camelResponse.branchId);

    // Start interval checking news
    subscribeRedisPubsub(camelResponse.accountId, camelResponse.branchId);
    store.dispatch(PubSubActions.startCheckingNews(prefixKey, loginBranchInfo.domain));

    return true;
  } catch (error) {
    console.warn(error); // eslint-disable-line no-console
    handlingLogoutBranch(prefixKey);
  }

  return false;
};

const STATE_CONTROL_NAME = {
  login: "authLoginStatus",
  authVerify: "authVerifyStatus",
};

function* handleErrorResponse(response, key) {
  const errorResponse = {};
  switch (response.status) {
    case ApiConstant.STT_OK:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.request;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_CREATED:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.created;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_BAD_REQUEST:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.wrongOtp;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_OTP_EXPIRED:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.expiredOtp;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_FORBIDDEN:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.limitResend;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_NOT_FOUND:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.notFoundPhone;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_BLOCK:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.block;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    case ApiConstant.STT_LIMIT_DEVICE:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.limitDevice;
      yield put(AuthActions.authSuccess(errorResponse));
      break;

    default:
      errorResponse[key] = AppConstant.VERIFY_OTP_ERROR_TYPE.systemError;
      yield put(AuthActions.authSuccess({ ...errorResponse, ...response.data }));
      break;
  }
}
